import {
  ApprovePromptRuleResultSchema,
  ConfirmKbProposalResultSchema,
  type CsReview,
  type CsReviewProposalList,
  CsReviewProposalListSchema,
  CsReviewSchema,
  type CsReviewScoreWeeks,
  CsReviewScoreWeeksSchema,
  type OpenCsReview,
  type SessionDetail,
  SessionDetailSchema,
} from '@sbc/domain-models/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiGet, apiSend } from './client';

const CS_REVIEWS_KEY = ['admin', 'cs-reviews'];
const BASE = '/api/admin/cs-reviews';

export function useOpenCsReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OpenCsReview) => apiSend('POST', BASE, input, CsReviewSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CS_REVIEWS_KEY }),
  });
}

export function useCsReview(sessionId: string) {
  return useQuery<CsReview>({
    queryKey: [...CS_REVIEWS_KEY, 'review', sessionId],
    queryFn: () => apiGet(`${BASE}/${sessionId}`, CsReviewSchema),
  });
}

export function useCsReviewSession(sessionId: string) {
  return useQuery<SessionDetail>({
    queryKey: [...CS_REVIEWS_KEY, 'session', sessionId],
    queryFn: () => apiGet(`${BASE}/${sessionId}/session`, SessionDetailSchema),
  });
}

export function useSendCsReviewMessage(sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: string | null) => {
      if (content !== null) {
        await apiSend('POST', `${BASE}/${sessionId}/messages`, { content }, SessionDetailSchema);
      }
      return apiSend('POST', `${BASE}/${sessionId}/run`, {}, SessionDetailSchema);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CS_REVIEWS_KEY }),
  });
}

export function useCsReviewProposals(filter: { sessionId?: string; pending?: boolean }) {
  const params = new URLSearchParams();
  if (filter.sessionId) {
    params.set('sessionId', filter.sessionId);
  }
  if (filter.pending) {
    params.set('pending', 'true');
  }
  return useQuery<CsReviewProposalList>({
    queryKey: [...CS_REVIEWS_KEY, 'proposals', filter],
    queryFn: () => apiGet(`${BASE}/proposals?${params.toString()}`, CsReviewProposalListSchema),
  });
}

export function useDecideCsReviewProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'confirm' | 'approve' | 'reject' }) => {
      const path = `${BASE}/proposals/${id}/${action}`;
      if (action === 'confirm') {
        return apiSend('POST', path, {}, ConfirmKbProposalResultSchema);
      }
      if (action === 'approve') {
        return apiSend('POST', path, {}, ApprovePromptRuleResultSchema);
      }
      return apiSend('POST', path, {}, z.unknown());
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CS_REVIEWS_KEY }),
  });
}

export function useCsReviewScores() {
  return useQuery<CsReviewScoreWeeks>({
    queryKey: [...CS_REVIEWS_KEY, 'scores'],
    queryFn: () => apiGet(`${BASE}/scores`, CsReviewScoreWeeksSchema),
  });
}
